/**
 * New task modal — V3 R3.
 *
 * Quick-add stays for speed; this is the complete path for a big, detailed work
 * item. Every §6.5 field is here.
 *
 * Attachments are the one place needing care: the presign flow binds a file to
 * an entity id, and the task does not exist yet. Files are therefore held in
 * memory and uploaded immediately *after* create, before the modal closes — so
 * a failed upload is reported against a task that exists rather than orphaning
 * bytes against an id that never will.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { FileUp, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Priority, TaskType } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { RichTextEditor, type RichTextEditorHandle } from '@/components/shared/RichTextEditor';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useProject, useProjects } from '@/features/projects';
import { useProjectSprints } from '@/features/views';
import { useUiStore } from '@/stores/ui';
import {
  DateField,
  EstimateField,
  LabelPicker,
  PeoplePicker,
  PrioritySelect,
  StatusSelect,
  TaskPicker,
  TypeSelect,
} from './fields';
import { useCreateTask, useUploadAttachment, type TaskView } from './api';

interface Draft {
  projectId: string;
  title: string;
  description?: Record<string, unknown>;
  type: TaskType;
  statusId?: string;
  priority: Priority;
  assigneeIds: string[];
  reviewerId: string[];
  watcherIds: string[];
  labelIds: string[];
  sprintId: string;
  startDate: string | null;
  dueDate: string | null;
  estimateMinutes: number | null;
  checklist: string[];
  blockedBy: TaskView[];
  blocks: TaskView[];
}

const emptyDraft = (projectId: string): Draft => ({
  projectId,
  title: '',
  type: 'feature',
  priority: 'P2',
  assigneeIds: [],
  reviewerId: [],
  watcherIds: [],
  labelIds: [],
  sprintId: '',
  startDate: null,
  dueDate: null,
  estimateMinutes: null,
  checklist: [],
  blockedBy: [],
  blocks: [],
});

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-start gap-2">
      <span className="pt-1.5 text-[12px] vt-muted">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

export function NewTaskModal() {
  const open = useUiStore((s) => s.newTaskOpen);
  const setOpen = useUiStore((s) => s.setNewTaskOpen);
  const { user } = useAuth();
  const { data: projects = [] } = useProjects();
  const navigate = useNavigate();

  const create = useCreateTask();
  const upload = useUploadAttachment();
  const editorRef = useRef<RichTextEditorHandle>(null);

  const [draft, setDraft] = useState<Draft>(() => emptyDraft(''));
  const [files, setFiles] = useState<File[]>([]);
  const [checklistText, setChecklistText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const { data: project } = useProject(draft.projectId || undefined);
  const { data: sprints = [] } = useProjectSprints(draft.projectId || undefined);

  // Default to the user's quick-add project, else the first they can see.
  useEffect(() => {
    if (!open) return;
    setDraft((d) =>
      d.projectId
        ? d
        : { ...d, projectId: user?.preferences.defaultProjectId ?? projects[0]?._id ?? '' },
    );
  }, [open, projects, user?.preferences.defaultProjectId]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void =>
    setDraft((d) => ({ ...d, [key]: value }));

  function reset(): void {
    setDraft(emptyDraft(draft.projectId));
    setFiles([]);
    setChecklistText('');
    setError(null);
  }

  async function submit(andOpen: boolean, another = false): Promise<void> {
    setError(null);
    if (!draft.projectId) {
      setError('Pick a project — a task cannot exist outside one');
      return;
    }
    if (!draft.title.trim()) {
      setError('Give the task a title');
      return;
    }

    try {
      const description = editorRef.current?.getJSON();
      const hasBody = ((description?.content as unknown[]) ?? []).length > 0;

      const task = await create.mutateAsync({
        projectId: draft.projectId,
        title: draft.title.trim(),
        ...(hasBody ? { description } : {}),
        type: draft.type,
        ...(draft.statusId ? { statusId: draft.statusId } : {}),
        priority: draft.priority,
        assigneeIds: draft.assigneeIds,
        ...(draft.reviewerId[0] ? { reviewerId: draft.reviewerId[0] } : {}),
        watcherIds: draft.watcherIds,
        labelIds: draft.labelIds,
        ...(draft.sprintId ? { sprintId: draft.sprintId } : {}),
        ...(draft.startDate ? { startDate: draft.startDate } : {}),
        ...(draft.dueDate ? { dueDate: draft.dueDate } : {}),
        ...(draft.estimateMinutes ? { estimateMinutes: draft.estimateMinutes } : {}),
        checklist: draft.checklist.map((text) => ({ text })),
        blockedBy: draft.blockedBy.map((t) => t._id),
        blocks: draft.blocks.map((t) => t._id),
      });

      // Attachments bind once the task has an id.
      if (files.length > 0) {
        const results = await Promise.allSettled(
          files.map((file) => upload.mutateAsync({ file, entityType: 'task', entityId: task._id })),
        );
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          toast.error(
            `${task.key} created, but ${failed} file${failed === 1 ? '' : 's'} failed to upload`,
          );
        }
      }

      toast.success(`Created ${task.key}`);

      if (another) {
        reset();
        return;
      }
      setOpen(false);
      reset();
      if (andOpen) navigate(`/tasks/${task.key}`);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.details?.[0]?.message ?? err.message) : 'Could not create the task',
      );
    }
  }

  const busy = create.isPending || upload.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        setOpen(next);
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
        <DialogTitle className="mb-3 text-[16px] font-semibold">New task</DialogTitle>

        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit(false);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]);
          }}
        >
          <Input
            autoFocus
            aria-label="Title"
            data-testid="new-task-title"
            placeholder="What needs doing?"
            className="h-10 text-[15px]"
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
          />

          <RichTextEditor
            handleRef={editorRef}
            data-testid="new-task-description"
            minHeight={110}
            placeholder="Context, acceptance criteria, links. #VL-142 links a task."
          />

          <div className="grid gap-2 sm:grid-cols-2">
            <Row label="Project">
              <select
                aria-label="Project"
                data-testid="new-task-project"
                value={draft.projectId}
                onChange={(e) => setDraft((d) => ({ ...d, projectId: e.target.value, statusId: undefined }))}
                className="h-8 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              >
                <option value="">Select…</option>
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.icon} {p.name}
                  </option>
                ))}
              </select>
            </Row>

            <Row label="Status">
              <StatusSelect
                statuses={project?.statuses ?? []}
                value={draft.statusId ?? project?.statuses?.[0]?.id}
                onChange={(statusId) => set('statusId', statusId)}
                testId="new-task-status"
              />
            </Row>

            <Row label="Type">
              <TypeSelect value={draft.type} onChange={(t) => set('type', t)} />
            </Row>

            <Row label="Priority">
              <PrioritySelect value={draft.priority} onChange={(p) => set('priority', p)} />
            </Row>

            <Row label="Assignees">
              <PeoplePicker
                label="Assignees"
                testId="new-task-assignees"
                value={draft.assigneeIds}
                onChange={(ids) => set('assigneeIds', ids)}
                suggestFor={{
                  projectId: draft.projectId,
                  type: draft.type,
                  estimateMinutes: draft.estimateMinutes ?? undefined,
                  dueDate: draft.dueDate ?? undefined,
                }}
              />
            </Row>

            <Row label="Reviewer">
              <PeoplePicker
                label="Reviewer"
                testId="new-task-reviewer"
                multiple={false}
                value={draft.reviewerId}
                onChange={(ids) => set('reviewerId', ids)}
              />
            </Row>

            <Row label="Watchers">
              <PeoplePicker
                label="Watchers"
                testId="new-task-watchers"
                value={draft.watcherIds}
                onChange={(ids) => set('watcherIds', ids)}
              />
            </Row>

            <Row label="Labels">
              <LabelPicker value={draft.labelIds} onChange={(ids) => set('labelIds', ids)} />
            </Row>

            <Row label="Start">
              <DateField label="Start date" value={draft.startDate} onChange={(v) => set('startDate', v)} />
            </Row>

            <Row label="Due">
              <DateField
                label="Due date"
                testId="new-task-due"
                value={draft.dueDate}
                onChange={(v) => set('dueDate', v)}
              />
            </Row>

            <Row label="Estimate">
              <EstimateField
                minutes={draft.estimateMinutes}
                onChange={(m) => set('estimateMinutes', m)}
              />
            </Row>

            <Row label="Sprint">
              <select
                aria-label="Sprint"
                data-testid="new-task-sprint"
                value={draft.sprintId}
                onChange={(e) => set('sprintId', e.target.value)}
                className="h-8 w-full rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              >
                <option value="">Backlog</option>
                {/* Completed sprints are omitted: committing new work to a
                    sprint that has already closed is never what anyone means. */}
                {sprints
                  .filter((sprint) => sprint.status !== 'completed')
                  .map((sprint) => (
                    <option key={sprint._id} value={sprint._id}>
                      {sprint.name}
                      {sprint.status === 'active' ? ' (active)' : ''}
                    </option>
                  ))}
              </select>
            </Row>
          </div>

          <Row label="Checklist">
            <div className="flex flex-col gap-1">
              {draft.checklist.map((text, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[13px]">
                  <input type="checkbox" disabled aria-hidden />
                  <span className="flex-1 truncate">{text}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${text}`}
                    onClick={() => set('checklist', draft.checklist.filter((_, j) => j !== i))}
                    className="grid size-5 place-items-center rounded-[4px] vt-muted hover:bg-[var(--surface-2)]"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              ))}
              <div className="flex gap-1">
                <Input
                  aria-label="Add a checklist item"
                  data-testid="new-task-checklist-input"
                  className="h-7"
                  placeholder="Add a step and press Enter"
                  value={checklistText}
                  onChange={(e) => setChecklistText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (!checklistText.trim()) return;
                    set('checklist', [...draft.checklist, checklistText.trim()]);
                    setChecklistText('');
                  }}
                />
              </div>
            </div>
          </Row>

          <Row label="Dependencies">
            <div className="flex flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <TaskPicker
                  label="+ Blocked by"
                  projectId={draft.projectId}
                  onPick={(t) => set('blockedBy', [...draft.blockedBy, t])}
                  testId="new-task-blocked-by"
                />
                {draft.blockedBy.map((t) => (
                  <span
                    key={t._id}
                    className="vt-mono flex items-center gap-1 rounded-[4px] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]"
                  >
                    {t.key}
                    <button
                      type="button"
                      aria-label={`Remove ${t.key}`}
                      onClick={() => set('blockedBy', draft.blockedBy.filter((b) => b._id !== t._id))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <TaskPicker
                  label="+ Blocks"
                  projectId={draft.projectId}
                  onPick={(t) => set('blocks', [...draft.blocks, t])}
                />
                {draft.blocks.map((t) => (
                  <span
                    key={t._id}
                    className="vt-mono flex items-center gap-1 rounded-[4px] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px]"
                  >
                    {t.key}
                    <button
                      type="button"
                      aria-label={`Remove ${t.key}`}
                      onClick={() => set('blocks', draft.blocks.filter((b) => b._id !== t._id))}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </Row>

          <Row label="Attachments">
            <div
              className={`rounded-[6px] border border-dashed p-2 transition-colors duration-[120ms] ${
                dragging ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border)]'
              }`}
              data-testid="new-task-dropzone"
            >
              <label className="flex cursor-pointer items-center gap-2 text-[12px] vt-muted">
                <FileUp className="size-4" />
                Drop files here, or choose
                <input
                  type="file"
                  multiple
                  className="hidden"
                  data-testid="new-task-files"
                  onChange={(e) => setFiles((f) => [...f, ...Array.from(e.target.files ?? [])])}
                />
              </label>
              {files.length > 0 && (
                <ul className="mt-1.5 flex flex-col gap-1">
                  {files.map((file, i) => (
                    <li key={i} className="flex items-center gap-2 text-[12px]">
                      <span className="flex-1 truncate">{file.name}</span>
                      <span className="vt-mono vt-muted">{Math.ceil(file.size / 1024)} KB</span>
                      <button
                        type="button"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => setFiles((f) => f.filter((_, j) => j !== i))}
                      >
                        <X className="size-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Row>

          {error && (
            <p
              role="alert"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {error}
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="subtle" disabled={busy} onClick={() => void submit(false, true)}>
              Create &amp; add another
            </Button>
            <Button type="button" variant="subtle" disabled={busy} onClick={() => void submit(true)}>
              Create &amp; open
            </Button>
            <Button type="submit" variant="primary" disabled={busy} data-testid="new-task-submit">
              {busy ? <Loader2 className="animate-spin" /> : <Plus />}
              Create task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
